// LLM:METADATA
// :topology: [Domain:Error_Management | Role:API_Error_Handler | Pattern:Service]
// :relates-to: uses: "api.js", uses: "logger.js", uses: "Informers.js", required-by: "All Vue components"
// :rationale: Centralizes all error capture, logging, and user notification.
// :references: PRD: "BUG-HIGH-001", SPEC: "add-error-handling-to-api-calls.md"
// :contract: invariant: "All errors passed to handle() are logged AND shown to user."
// :decision_cache: "Chose centralized service over per-component handling for consistency and maintainability [ERR-001]"
// LLM:END

import router from "@/router"
import axios from "axios"
import logger from "./logger"
import { notify } from "@/helpers/Informers"
import { setTopologyId } from "@/services/logger"
import { TOPOLOGY_ERROR_HANDLER } from "@/constants/topology"
import authStorage from "@/services/authStorage"
import {
  ERROR_HANDLE_START,
  ERROR_HANDLE_COMPLETE,
  ERROR_NOTIFY_START,
  ERROR_NOTIFY_COMPLETE,
} from "@/constants/events"

setTopologyId(TOPOLOGY_ERROR_HANDLER)

// Flag to prevent multiple 401 redirects and loops
let isRedirecting = false

/**
 * Dev mode: console-only errors, no toast. True when NODE_ENV is development
 * OR when running on localhost (covers production build served locally).
 */
function isDevMode() {
  if (typeof window === "undefined")
    return process.env.NODE_ENV === "development"
  return (
    process.env.NODE_ENV === "development" ||
    window.location?.hostname === "localhost" ||
    window.location?.hostname === "127.0.0.1" ||
    window.location?.hostname === "host.docker.internal"
  )
}

/**
 * Read one header value from plain-object or AxiosHeaders containers.
 *
 * @precondition headers is nullish, a plain object, or an Axios headers-like container.
 * @postcondition Returns the header string when present; otherwise returns an empty string.
 * @mutates none
 */
function getHeaderValue(headers, name) {
  if (!headers) return ""

  if (typeof headers.get === "function") {
    return String(headers.get(name) || headers.get(name.toLowerCase()) || "")
  }

  return String(headers[name] || headers[name.toLowerCase()] || "")
}

/**
 * Normalize a user-visible support code to the bounded request-id contract.
 *
 * @precondition value is any candidate evidence id from backend meta or transport headers.
 * @postcondition Returns the candidate only when it matches the safe request-id pattern.
 * @mutates none
 */
function normalizeSupportCode(value) {
  const candidate = typeof value === "string" ? value.trim() : ""
  if (!candidate) return ""

  return /^[a-z0-9][a-z0-9._:-]{7,127}$/i.test(candidate) ? candidate : ""
}

/**
 * Extract one safe support code from backend error metadata or transport correlation headers.
 *
 * @precondition error is an axios-style error object or a compatible partial shape.
 * @postcondition Returns the first safe support code from response meta, response headers, or request headers.
 * @mutates none
 */
function extractSupportCode(error) {
  return (
    normalizeSupportCode(error?.response?.data?.meta?.request_id) ||
    normalizeSupportCode(
      getHeaderValue(error?.response?.headers, "X-Request-ID")
    ) ||
    normalizeSupportCode(getHeaderValue(error?.config?.headers, "X-Request-ID"))
  )
}

/**
 * Build a generic user-facing API failure message with an optional support code.
 *
 * @precondition fallbackMessage is already safe for end-user display.
 * @postcondition Never includes backend exception details, stack traces, or event ids.
 * @mutates none
 */
function formatSupportMessage(fallbackMessage, supportCode) {
  if (!supportCode) return fallbackMessage
  return `${fallbackMessage} Код обращения: ${supportCode}.`
}

export const errorHandler = {
  // LLM:METADATA
  // :topology: [Domain:Error_Management | Role:API_Error_Handler | Pattern:Service]
  // :relates-to: uses: "logger.js", "Informers.js", required-by: "All Vue components"
  // :rationale: "Centralizes all error capture, logging, and user notification"
  // :contract: pre: "Error object received", post: "Error logged and user notified", invariant: "Promise rejected for error propagation"
  // :decision_cache: "Chose centralized service over per-component handling for consistency and maintainability [ERR-001]"
  // LLM:END
  /**
   * Handle API errors with appropriate user feedback
   * @param {Error} error - The error object from axios
   * @returns {Promise<never>} - Rejected promise for error propagation
   */
  handle(error) {
    // Ignore request cancellations
    if (axios.isCancel(error) || error.name === "AbortError") {
      return
    }

    // Dev Mode: Raw console output for better debugging (no visual toast)
    if (isDevMode()) {
      console.error("[ErrorHandler]", error)
    }

    logger.info(ERROR_HANDLE_START, "Handling error", {
      message: error.message,
      hasResponse: !!error.response,
      hasRequest: !!error.request,
    })

    if (error.response) {
      // Server responded with error status
      const status = error.response.status
      const responseErrors = error.response.data?.errors
      const supportCode = extractSupportCode(error)
      const message =
        error.response.data?.message ||
        (Array.isArray(responseErrors) && responseErrors.length
          ? String(responseErrors[0])
          : "Server error")

      switch (status) {
        case 400:
          this.showError("Bad request: " + message)
          logger.error(ERROR_HANDLE_COMPLETE, "Bad request error", {
            status,
            message,
          })
          break
        case 401:
          // Unauthorized - redirect to login
          this.redirectToLogin()
          logger.error(ERROR_HANDLE_COMPLETE, "Unauthorized error", {
            status,
          })
          break
        case 403:
          this.showError(
            message === "Server error"
              ? "Недостаточно прав для этого действия."
              : message
          )
          logger.error(ERROR_HANDLE_COMPLETE, "Forbidden error", {
            status,
          })
          break
        case 404:
          this.showError("Resource not found.")
          logger.error(ERROR_HANDLE_COMPLETE, "Not found error", {
            status,
          })
          break
        case 500:
          this.showError(
            formatSupportMessage(
              "Server error. Please try again later.",
              supportCode
            )
          )
          logger.error(ERROR_HANDLE_COMPLETE, "Server error", {
            status,
            supportCode,
          })
          break
        default:
          if (status >= 500) {
            this.showError(
              formatSupportMessage(
                "Server error. Please try again later.",
                supportCode
              )
            )
          } else {
            this.showError("Error: " + message)
          }
          logger.error(ERROR_HANDLE_COMPLETE, "Unknown error", {
            status,
            message,
            supportCode,
          })
      }
    } else if (error.request) {
      // Request made but no response
      this.showError("Network error. Please check your connection.")
      logger.error(ERROR_HANDLE_COMPLETE, "Network error", {
        message: error.message,
      })
    } else {
      // Error in request setup (message can be undefined for non-Error rejections)
      const msg = error?.message ?? "Unknown error"
      this.showError("Request error: " + msg)
      logger.error(ERROR_HANDLE_COMPLETE, "Request setup error", {
        message: error.message,
      })
    }
  },

  /**
   * Show error notification to user
   * @param {string} message - Error message to display
   */
  showError(message) {
    // LLM:METADATA
    // :topology: [Domain:Error_Management | Role:UI_Feedback | Pattern:Notification]
    // :rationale: Replaced blocking alert() with non-blocking console logging, semantic trace events, and Toast notification.
    // :contract: pre: "Error message exists", post: "User notified via console and toast", invariant: "No JS execution block"
    // LLM:END
    logger.info(ERROR_NOTIFY_START, "Showing error notification to user", {
      message,
    })

    if (isDevMode()) {
      console.warn("[ErrorHandler]", message)
    }
    notify(message, "error")

    logger.info(ERROR_NOTIFY_COMPLETE, "Error notification shown", {
      message,
    })
  },

  /**
   * Redirect to login page
   * Clears authentication tokens and redirects using SPA router
   */
  redirectToLogin() {
    // LLM:METADATA
    // :topology: [Domain:Authentication | Role:Redirection_Logic | Pattern:Auth_Guard]
    // :rationale: Uses Vue Router for SPA navigation instead of full page reload to prevent infinite loops.
    // :contract: pre: "401 detected", post: "User redirected to /login", invariant: "No duplicate redirects (isRedirecting flag)"
    // LLM:END

    // Prevent multiple redirects during the same flow
    if (isRedirecting) {
      return
    }
    isRedirecting = true

    logger.info(ERROR_HANDLE_COMPLETE, "Redirecting to login", {
      reason: "401 unauthorized",
      hasToken: !!authStorage.getToken(),
    })

    authStorage.clearAuthStorage()

    // Use router for SPA navigation instead of window.location.href
    router.push("/login").finally(() => {
      // Small delay to ensure navigation is processed before reset
      setTimeout(() => {
        isRedirecting = false
      }, 500)
    })
  },
}
export default errorHandler
